import {
  MAX_TAGS_PER_IMAGE,
  MAX_TAG_NAME_LENGTH,
  type TagRef,
  getAPIErrorCode,
  getAPIErrorMessage,
  useAddImageTagMutation,
  useRemoveImageTagMutation,
} from '@vizit/api';
import { AlertLevel, Menu, MenuItem, Popover, useToast } from '@vizit/atoms';
import { PlusIcon, XCloseIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { Autocomplete, Button, Group } from 'react-aria-components';
import { TagSearchField, useTagSearch } from '../tag-search/tag-search';

const CREATE_KEY = '__create__';

const LIMIT_REACHED = 'Maximum of 8 tags per image';

const keepOpenAfterPick = () => {};

const chipClasses = 'inline-flex h-11 items-center bg-secondary px-3 text-xs text-secondary';

interface ImageTagsProps {
  image: { id: string; name: string };
  tags: TagRef[];
  workspaceId: string;
  className?: string;
}

function ImageTags(props: ImageTagsProps) {
  const { image, tags, workspaceId, className } = props;
  const { addToast } = useToast();
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);
  const [pickCount, setPickCount] = React.useState(0);
  const [search, setSearch] = React.useState('');

  const showError = (title: string, error: unknown) => {
    const isLimit = getAPIErrorCode(error) === 'TAG_LIMIT_EXCEEDED';
    addToast({
      title,
      description: isLimit ? LIMIT_REACHED : getAPIErrorMessage(error, 'Something went wrong. Please try again.'),
      level: AlertLevel.Error,
    });
  };

  const { mutate: addTag } = useAddImageTagMutation({
    onError: (error) => showError("Couldn't add the tag", error),
  });
  const { mutate: removeTag } = useRemoveImageTagMutation({
    onError: (error) => showError("Couldn't remove the tag", error),
  });

  const isFull = tags.length >= MAX_TAGS_PER_IMAGE;

  return (
    <Group className={cx('flex flex-wrap items-center gap-2 pt-3', className)} aria-label={`Tags of ${image.name}`}>
      {tags.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          onRemove={() => removeTag({ workspace_id: workspaceId, image_id: image.id, tag })}
        />
      ))}
      <span className="inline-flex" title={isFull ? LIMIT_REACHED : undefined}>
        <Popover.Trigger isOpen={isPickerOpen} onOpenChange={setIsPickerOpen}>
          <Button
            aria-label={isFull ? `Add a tag to ${image.name}, ${LIMIT_REACHED}` : `Add a tag to ${image.name}`}
            isDisabled={isFull}
            className={cx(
              chipClasses,
              'w-11 justify-center px-0 outline-none',
              'data-[hovered]:bg-secondary-hover data-[focus-visible]:ring-2 data-[focus-visible]:ring-brand-600',
              'data-[disabled]:cursor-not-allowed data-[disabled]:text-disabled',
            )}
          >
            <PlusIcon size={12} aria-hidden />
          </Button>
          <Popover placement="bottom start" offset={4} className="w-[280px] shadow-lg">
            <Popover.Dialog aria-label={`Add a tag to ${image.name}`}>
              <TagPicker
                key={pickCount}
                workspaceId={workspaceId}
                tags={tags}
                search={search}
                onSearchChange={setSearch}
                onPick={(tag) => {
                  if (tags.length + 1 >= MAX_TAGS_PER_IMAGE) setIsPickerOpen(false);
                  const typed = search;
                  setSearch('');
                  setPickCount((count) => count + 1);
                  addTag(
                    { workspace_id: workspaceId, image_id: image.id, tag },
                    { onError: () => setSearch((current) => current || typed) },
                  );
                }}
              />
            </Popover.Dialog>
          </Popover>
        </Popover.Trigger>
      </span>
    </Group>
  );
}

function TagChip({ tag, onRemove }: { tag: TagRef; onRemove: () => void }) {
  return (
    <span className={cx(chipClasses, 'group max-w-full gap-1')}>
      <span className="truncate">{tag.name}</span>
      <Button
        aria-label={`Remove tag ${tag.name}`}
        onPress={onRemove}
        className={cx(
          'flex h-8 w-0 shrink-0 items-center justify-center overflow-hidden text-quinary opacity-0 outline-none',
          'group-hover:w-8 group-hover:opacity-100 data-[focus-visible]:w-8 data-[focus-visible]:opacity-100',
          'data-[focus-visible]:ring-2 data-[focus-visible]:ring-brand-600 data-[hovered]:text-secondary',
          '[@media(pointer:coarse)]:w-8 [@media(pointer:coarse)]:opacity-100',
        )}
      >
        <XCloseIcon size={12} aria-hidden />
      </Button>
    </span>
  );
}

interface TagPickerProps {
  workspaceId: string;
  tags: TagRef[];
  search: string;
  onSearchChange: (search: string) => void;
  onPick: (tag: TagRef | { name: string }) => void;
}

function TagPicker(props: TagPickerProps) {
  const { workspaceId, tags, search, onSearchChange, onPick } = props;
  const { name, tags: matches, isSettled } = useTagSearch(workspaceId, search);

  const onImage = new Set(tags.map((tag) => tag.name));
  const options = matches.filter((tag) => !onImage.has(tag.name));
  const isTaken = onImage.has(name) || options.some((tag) => tag.name === name);
  const canCreate = !!name && !isTaken;

  const items: { id: string; name: string }[] = [
    ...options.map(({ id, name: tagName }) => ({ id, name: tagName })),
    ...(canCreate ? [{ id: CREATE_KEY, name: search.trim() }] : []),
  ];

  return (
    <Autocomplete inputValue={search} onInputChange={onSearchChange}>
      <TagSearchField label="Search or create tag" maxLength={MAX_TAG_NAME_LENGTH} />
      <Menu
        aria-label="Tags"
        items={items}
        onClose={keepOpenAfterPick}
        className="max-h-[228px] overflow-y-auto py-2"
        onAction={(key) => {
          const tag = options.find(({ id }) => id === key);
          onPick(tag ? { id: tag.id, name: tag.name } : { name: search });
        }}
        renderEmptyState={() => (
          <p className="px-8 py-5 text-sm text-tertiary">
            {onImage.has(name) ? 'Already on this image' : isSettled ? 'Type to search or create' : 'Searching…'}
          </p>
        )}
      >
        {(item) =>
          item.id === CREATE_KEY ? (
            <MenuItem id={item.id} textValue={`Create ${item.name}`} className="font-semibold">
              <PlusIcon size={16} aria-hidden />
              <span className="truncate">Create “{item.name}”</span>
            </MenuItem>
          ) : (
            <MenuItem id={item.id} textValue={item.name}>
              <span className="truncate">{item.name}</span>
            </MenuItem>
          )
        }
      </Menu>
    </Autocomplete>
  );
}

export { ImageTags };
export type { ImageTagsProps };
